import React, { forwardRef } from 'react';
import { 
  Box, Typography, Table, TableBody, TableCell, TableHead, 
  TableRow, Paper, Grid, Divider, Chip
} from '@mui/material';
import { formatDateForReport, formatCurrencyForReport } from '../../utils/pdfUtils';

const AllTenantsFinancialReport = forwardRef(({ tenants, paymentStats, transactions, reportType = 'all' }, ref) => {
  // Generate formatted report date
  const reportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <Box ref={ref} sx={{ 
      p: 4, 
      width: '210mm',
      minHeight: '297mm',
      backgroundColor: 'white',
      color: 'black',
      fontFamily: 'Arial, sans-serif',
      fontSize: '10pt'
    }}>
      {/* Report Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#3f51b5' }}>
          System-Wide Financial Report
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          All Organizations Financial Summary
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Generated on {reportDate}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* System-Wide Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          System Overview
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Total Organizations:</strong> {tenants?.length || 0}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Active Organizations:</strong> {tenants?.filter(t => t.status === 'active')?.length || 0}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>System Status:</strong> OPERATIONAL
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Report Scope:</strong> All Organizations
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Financial Summary */}
      {(reportType === 'all' || reportType === 'revenue') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Financial Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Total Revenue
                </Typography>
                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  {formatCurrencyForReport(paymentStats?.totalRevenue || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Monthly Revenue
                </Typography>
                <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                  {formatCurrencyForReport(paymentStats?.monthlyRevenue || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Active Subscriptions
                </Typography>
                <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                  {paymentStats?.activeSubscriptions || 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Organization Summary */}
      {(reportType === 'all') && tenants && tenants.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#673ab7', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Organization Overview
          </Typography>
          <Table sx={{ mb: 2 }}>
            <TableHead sx={{ bgcolor: 'rgba(103, 58, 183, 0.1)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Subdomain</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Admin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                  <TableCell sx={{ fontWeight: 'medium' }}>{tenant.name}</TableCell>
                  <TableCell>{tenant.subdomain}</TableCell>
                  <TableCell>
                    <Box sx={{ 
                      display: 'inline-block', 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: '4px', 
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: tenant.status === 'active' ? '#2e7d32' : 
                             tenant.status === 'pending' ? '#ed6c02' : '#d32f2f',
                      bgcolor: tenant.status === 'active' ? '#e8f5e9' : 
                               tenant.status === 'pending' ? '#fff3e0' : '#ffebee'
                    }}>
                      {tenant.status?.toUpperCase() || 'UNKNOWN'}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDateForReport(tenant.createdAt)}</TableCell>
                  <TableCell>{tenant.adminEmail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Recent Transactions */}
      {(reportType === 'all' || reportType === 'transactions') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#ff9800', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Recent Transactions
          </Typography>
          
          {paymentStats?.recentTransactions && paymentStats.recentTransactions.length > 0 ? (
            <Table sx={{ mb: 2 }}>
              <TableHead sx={{ bgcolor: '#fff3e0' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tenant</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Transaction ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentStats.recentTransactions.map((tx, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                    <TableCell>{tx.tenantName}</TableCell>
                    <TableCell>{tx.id}</TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {formatCurrencyForReport(tx.amount)}
                    </TableCell>
                    <TableCell>{tx.type}</TableCell>
                    <TableCell>{formatDateForReport(tx.date)}</TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'inline-block', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: '4px', 
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        color: tx.status === 'successful' ? '#2e7d32' : '#d32f2f',
                        bgcolor: tx.status === 'successful' ? '#e8f5e9' : '#ffebee'
                      }}>
                        {tx.status}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : transactions && transactions.length > 0 ? (
            <Table sx={{ mb: 2 }}>
              <TableHead sx={{ bgcolor: '#fff3e0' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Organization</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.slice(0, 20).map((transaction, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                    <TableCell>{transaction.tenantName || 'N/A'}</TableCell>
                    <TableCell>{formatDateForReport(transaction.date || transaction.createdAt)}</TableCell>
                    <TableCell sx={{ 
                      color: transaction.type === 'refund' ? '#d32f2f' : '#2e7d32', 
                      fontWeight: 'medium' 
                    }}>
                      {formatCurrencyForReport(transaction.amount || 0)}
                    </TableCell>
                    <TableCell>{transaction.description || transaction.notes || 'No description'}</TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'inline-block', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: '4px', 
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        color: (transaction.status === 'completed' || transaction.status === 'successful') ? '#2e7d32' : '#d32f2f',
                        bgcolor: (transaction.status === 'completed' || transaction.status === 'successful') ? '#e8f5e9' : '#ffebee'
                      }}>
                        {transaction.status || 'completed'}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Paper sx={{ p: 3, bgcolor: '#f5f5f5', textAlign: 'center' }}>
              <Typography variant="body1">
                No transaction data available for reporting.
              </Typography>
            </Paper>
          )}
          
          {transactions && transactions.length > 20 && (
            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
              Showing 20 of {transactions.length} total transactions
            </Typography>
          )}
          
          {(paymentStats?.recentTransactions?.length > 0 || transactions?.length > 0) && (
            <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 'bold', mt: 2 }}>
              Total Transaction Value: {formatCurrencyForReport(
                (paymentStats?.recentTransactions && paymentStats.recentTransactions.length > 0) ?
                  paymentStats.recentTransactions.reduce((total, item) => total + (item.amount || 0), 0) :
                  transactions.reduce((total, item) => total + (item.amount || 0), 0)
              )}
            </Typography>
          )}
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          This is an official financial report generated from the system's records.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          For questions regarding this report, please contact support.
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.disabled' }}>
          Report ID: {Math.random().toString(36).substr(2, 9).toUpperCase()} | Page 1
        </Typography>
      </Box>
    </Box>
  );
});

AllTenantsFinancialReport.displayName = 'AllTenantsFinancialReport';

export default AllTenantsFinancialReport;
